<template>
  <div ref="form" class="e-form order pt5">
    <el-form label-position="top" ref="form" :model="container" size="mini">
      <el-row :gutter="10">
        <el-col :span="4">
          <el-form-item label="Trailer No.">
            <e-combobox
              v-if="inited"
              :options="trailerList"
              :input="{
                value: container.container_no,
                change: (value) => (container.container_no = value),
              }"
            />
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item label="Trailer Type">
            <el-select
              filterable
              clearable
              v-model="container.trailer_type"
              class="full-width"
            >
              <el-option
                v-for="item in options.trailerTypes"
                :key="item.name"
                :label="item.title"
                :value="item.name"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item label="Truck No.">
            <e-combobox
              v-if="inited"
              class="truck-combobox"
              :options="truckList"
              :input="{
                value: container.truck_no,
                change: (value) => (container.truck_no = value),
              }"
            />
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item label="Trip Date">
            <el-date-picker
              v-model="container.appt_date"
              type="date"
              :value-format="'yyyy-MM-dd'"
              class="full-width"
            >
            </el-date-picker>
          </el-form-item>
        </el-col>
        <el-col :span="5" v-if="!onlyApptDate">
          <div class="flex items-center gap-1">
            <el-form-item label="Start">
              <el-time-select
                v-model="container.appt_start"
                :disabled="!container.appt_date"
                :picker-options="timePickerOptions"
              />
            </el-form-item>
            <el-form-item label="End">
              <el-time-select
                v-model="container.appt_end"
                :disabled="!container.appt_start"
                :picker-options="{
                  ...timePickerOptions,
                  minTime: container.appt_start,
                }"
              />
            </el-form-item>
          </div>
        </el-col>
      </el-row>
      <div class="my-1 empty:hidden">
        <div class="payable-charges" v-if="$store.envs.access_payable">
          <charge-form
            type="payable"
            target="Ltl"
            :items="container.payable_charges"
            :errors="payableChargeErrors"
            key="payable"
          />
        </div>
      </div>
      <div class="routes my-1 bg-gray-50">
        <div
          class="border border-blue-200 mb-2 relative"
          v-for="(transport, index) in container.transports"
          :key="index"
        >
          <div class="header">
            <div class="show-hide -ml-1" :rel="`route${index + 1}`">
              <div class="font-semibold flex items-center gap-1">
                <i class="gray icon icons-min text-base" />
                <span class="text-xl">Route {{ index + 1 }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <popup
                componentPath="pages/bols/avail"
                width="100%"
                height="500px"
                :pivot-y="0"
                :click-to-close="false"
              >
                <el-button type="text">
                  <i class="fas fa-plus-circle" /> Avail. BOLs
                </el-button>
              </popup>
              <div v-if="$store.envs.access_payable">
                <slot v-if="transport.trucker_id">
                  <el-dropdown v-if="transport.trucker2_id">
                    <el-button type="text">
                      <i class="fas fa-plus-circle" /> Pay
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item
                        @click.native="
                          newCharge(transport.trucker_id, transport.sequence)
                        "
                      >
                        {{ transport.trucker.name }}
                      </el-dropdown-item>
                      <el-dropdown-item
                        @click.native="
                          newCharge(transport.trucker2_id, transport.sequence)
                        "
                      >
                        {{ transport.trucker2.name }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                  <el-button
                    v-else
                    @click="newCharge(transport.trucker_id, transport.sequence)"
                    type="text"
                  >
                    <i class="fas fa-plus-circle" /> Pay
                  </el-button>
                </slot>
                <slot v-if="transport.carrier.id">
                  <el-button
                    type="text"
                    @click="newCharge(transport.carrier.id, transport.sequence)"
                  >
                    <i class="fas fa-plus-circle" /> Pay
                  </el-button>
                </slot>
              </div>
              <container-dispatch
                :transport="transport"
                :allowBrokering="false"
              />
            </div>
          </div>
          <draggable
            :id="`route${index + 1}`"
            class="list-group operations"
            group="transport"
            ghost-class="ghost"
            handle=".handle"
            :list="transport.operations"
            @change="(evt) => moveOperation(evt, transport.sequence)"
          >
            <div
              v-for="(operation, index) in transport.operations"
              class="stop"
              :key="index"
              v-show="activeOperation(operation)"
            >
              <div class="grid grid-cols-2 gap-10 mx-2 mb-2 items-center">
                <div class="col-span-1">
                  <div class="flex items-center justify-between">
                    <div class="text-base font-semibold -ml-6">
                      <a class="handle">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 25 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M9.79492 5.17871C9.79492 6.28326 8.89947 7.17871 7.79492 7.17871C6.69037 7.17871 5.79492 6.28326 5.79492 5.17871C5.79492 4.07414 6.69037 3.17871 7.79492 3.17871C8.89947 3.17871 9.79492 4.07414 9.79492 5.17871ZM7.79492 14.1787C8.89947 14.1787 9.79492 13.2833 9.79492 12.1787C9.79492 11.0742 8.89947 10.1787 7.79492 10.1787C6.69037 10.1787 5.79492 11.0742 5.79492 12.1787C5.79492 13.2833 6.69037 14.1787 7.79492 14.1787ZM7.79492 21.1787C8.89947 21.1787 9.79492 20.2833 9.79492 19.1787C9.79492 18.0742 8.89947 17.1787 7.79492 17.1787C6.69037 17.1787 5.79492 18.0742 5.79492 19.1787C5.79492 20.2833 6.69037 21.1787 7.79492 21.1787Z"
                            fill="#828282"
                          />
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M18.7949 5.17871C18.7949 6.28326 17.8995 7.17871 16.7949 7.17871C15.6904 7.17871 14.7949 6.28326 14.7949 5.17871C14.7949 4.07414 15.6904 3.17871 16.7949 3.17871C17.8995 3.17871 18.7949 4.07414 18.7949 5.17871ZM16.7949 14.1787C17.8995 14.1787 18.7949 13.2833 18.7949 12.1787C18.7949 11.0742 17.8995 10.1787 16.7949 10.1787C15.6904 10.1787 14.7949 11.0742 14.7949 12.1787C14.7949 13.2833 15.6904 14.1787 16.7949 14.1787ZM16.7949 21.1787C17.8995 21.1787 18.7949 20.2833 18.7949 19.1787C18.7949 18.0742 17.8995 17.1787 16.7949 17.1787C15.6904 17.1787 14.7949 18.0742 14.7949 19.1787C14.7949 20.2833 15.6904 21.1787 16.7949 21.1787Z"
                            fill="#828282"
                          />
                        </svg>
                      </a>
                      {{ operation.name }}
                    </div>
                    <div
                      class="flex items-center gap-1 text-gray-400 hover:text-gray-800"
                    >
                      <slot v-if="operation.removable">
                        <el-popconfirm
                          title="Are you sure to delete this operation?"
                          confirmButtonText="Confirm"
                          cancelButtonText="Cancel"
                          @confirm="deleteOperation(operation)"
                        >
                          <a slot="reference" class="default" v-on:click.stop>
                            <svg
                              width="20"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                              ></path>
                            </svg>
                          </a>
                        </el-popconfirm>
                      </slot>
                    </div>
                  </div>
                </div>
                <div class="col-span-1">
                  <div class="flex items-center">
                    <div class="grow"> </div>
                    <div class="w-72">
                      <div
                        class="flex items-center justify-end text-sm font-bold"
                      >
                        <div class="mr-2">
                          {{ arrivalDepartureTime(operation) }}
                        </div>
                        <svg
                          width="20"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-10 items-center">
                <div class="col-span-1 flex items-center -ml-6">
                  <img
                    v-if="companyType(operation)"
                    class="company"
                    :src="$getSvgUrl(companyType(operation))"
                    :title="companyType(operation)"
                  />
                  <div class="text-base font-semibold px-2">
                    {{ findCompany(operation).name }}
                  </div>
                </div>
                <div class="col-span-1">
                  <div class="flex items-center justify-between">
                    <popup
                      componentPath="components/html"
                      width="960px"
                      :pivot-y="0"
                      :options="{ url: `/containers/${operation.bol_id}` }"
                    >
                      <a class="underline"> BOL: #{{ operation.bol_id }} </a>
                    </popup>
                    <div>{{ bolDetails(operation).customer.name }}</div>
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-10">
                <div class="col-span-1">
                  <div class="grid grid-cols-12 gap-1">
                    <div
                      class="col-span-5 contact"
                      v-show="operation.company_id"
                    >
                      <popup
                        componentPath="pages/maps/finder"
                        width="720px"
                        height="600px"
                        :options="{
                          address: companyAddress(operation, { html: false }),
                        }"
                        className="default font-semibold"
                      >
                        <div class="flex items-baseline gap-1 text-xs">
                          <i class="fas fa-map-marker-alt" />
                          <div
                            v-html="companyAddress(operation, { html: true })"
                          />
                        </div>
                      </popup>
                      <div
                        class="flex items-baseline gap-1 text-xs font-normal"
                      >
                        <i class="fas fa-phone" />
                        <div>
                          {{ findCompany(operation).phone }}
                          <span v-if="findCompany(operation).phone_extension">
                            ext. {{ findCompany(operation).phone_extension }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="col-span-5 ophours">
                      <slot v-if="operation.company_id">
                        <i class="fa-solid fa-clock mr-2" />
                        <span class="font-semibold">Operating Hrs</span>
                        <div class="text-[11px] leading-3">{{
                          findCompany(operation).ophours
                        }}</div>
                      </slot>
                    </div>
                    <div class="col-span-2">
                      <div class="flex items-center justify-end gap-1">
                        <el-popover
                          width="500"
                          placement="right"
                          trigger="hover"
                          :open-delay="1000"
                          v-if="
                            !isEmptyData(
                              findCompany(operation).extra_contact_info,
                            ) && findCompany(operation).type != 'Warehouse'
                          "
                        >
                          <div class="p-2">
                            <div
                              class="bg-gray-50 p-2 whitespace-pre-wrap"
                              v-html="findCompany(operation).extra_contact_info"
                            />
                          </div>
                          <a
                            class="text-gray-500 hover:text-gray-800"
                            slot="reference"
                          >
                            <svg
                              width="20"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                              ></path>
                            </svg>
                          </a>
                        </el-popover>
                        <note-entry
                          :options="companyOptions(operation)"
                          :key="operation.company_id"
                        />
                      </div>
                    </div>
                  </div>
                  <slot v-if="findCompany(operation).type == 'Warehouse'">
                    <p
                      class="px-4 py-1 text-xs leading-3 break-words whitespace-pre-wrap text-gray-800 italic"
                    >
                      {{ findCompany(operation).extra_contact_info }}
                    </p>
                  </slot>
                </div>
                <div class="col-span-1">
                  <div class="border border-orange-300 p-1 hover-field">
                    <label>
                      <span class="text-gray-500 text-xs">
                        {{ operation.instruction_label }}
                      </span>
                    </label>
                    <textarea
                      rows="3"
                      class="h-12 mini border-transparent"
                      v-model="operation.instructions"
                    />
                  </div>
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </div>
      <div class="border">
        <div class="bg-slate-100 p-2">Admin Comments</div>
        <div class="p-1">
          <note-widget
            v-if="id > 0"
            :id="id"
            type="Container"
            category="admin"
            size="small"
            mode="view"
          />
          <el-input
            type="textarea"
            :rows="id > 0 ? 3 : 6"
            placeholder="New Comment"
            v-model="container.admin_comment"
          />
        </div>
      </div>
      <el-form-item label=" ">
        <el-button size="mini" type="success" @click="saveLtl"
          >Save Order</el-button
        >
      </el-form-item>
    </el-form>
  </div>
</template>
<script>
import { format, isValid, parse } from "date-fns";
import api from "@/default/utils/api";
import { dateTimeComputed } from "@/default/utils/computed";

export default {
  name: "LtlForm",
  mixins: [api],
  props: {
    id: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      options: {
        trailers: [],
        trucks: [],
        trailerTypes: [],
      },
      timePickerOptions: {
        start: "00:00",
        step: "00:15",
        end: "23:45",
      },
      container: {
        transports: [],
        payable_charges: [],
      },
      inited: false,
      bols: [],
      companies: [],
      errors: {},
    };
  },
  mounted() {
    window.LtlForm = {
      addTrips: this.addTrips,
    };
  },
  async created() {
    await this.loadOptions();
    await this.loadLtl();
  },
  watch: {
    companyIds(nv, ov) {
      this.loadCompanies();
    },
    bolIds(nv, ov) {
      this.loadBols();
    },
  },
  computed: {
    ...dateTimeComputed,
    turboFrame() {
      return this.$refs.form.closest("turbo-frame");
    },
    trailerList() {
      return this.options.trailers.map((o) => {
        return { id: o.id, value: o.number };
      });
    },
    truckList() {
      return this.options.trucks.map((o) => {
        return { id: o.id, value: o.number, label: o.name };
      });
    },
    transport() {
      return this.container.transports[0];
    },
    companyIds() {
      return this.transport
        ? this.transport.operations.map((o) => o.company_id)
        : [];
    },
    bolIds() {
      return this.transport
        ? this.transport.operations.map((o) => o.bol_id)
        : [];
    },
    transports() {
      return this.container.transports.map((o) => {
        return this._.pick(o, [
          "id",
          "sequence",
          "trucker_id",
          "truck_id",
          "payable_party_id",
          "trucker2_id",
          "payable_party2_id",
          "tenant",
          "carrier_id",
        ]);
      });
    },
    operations() {
      return this.container.transports.map((o) => o.operations).flat();
    },
    findCompany() {
      return function (operation) {
        return this.companies.find((o) => o.id == operation.company_id) || {};
      };
    },
    companyType() {
      return function (operation) {
        return this.findCompany(operation)?.type?.toLowerCase();
      };
    },
    recordAttrs() {
      let record = Object.assign({}, this.container, {
        transports: this.transports,
        operations: this.operations,
      });
      record = this.nestedAttrs(record, "transports");
      record = this.nestedAttrs(record, "operations");
      record = this.nestedAttrs(record, "payable_charges", "key");
      return record;
    },
    payableChargeErrors() {
      return this.errors.payable_charges || {};
    },
    arrivalDepartureTime() {
      return function (operation) {
        const stamp = (value) => {
          if (!value) return null;
          const dateTime = parse(value, "yyyy-MM-dd HH:mm", new Date());
          if (isValid(dateTime)) {
            const date = format(dateTime, "MM/dd");
            const time = format(dateTime, this.timeFormat);
            return { date, time, text: `${date} ${time}` };
          }
          const dateOnly = parse(value, "yyyy-MM-dd", new Date());
          if (!isValid(dateOnly)) return null;
          const date = format(dateOnly, "MM/dd");
          return { date, time: "", text: date };
        };
        const arrival = stamp(operation.operated_at);
        const departure = stamp(operation.departed_at);
        const dts = [];
        if (arrival) dts.push(arrival.text);
        if (departure) {
          const sameDay = arrival && arrival.date == departure.date;
          dts.push(sameDay && departure.time ? departure.time : departure.text);
        }
        return dts.join(" ~ ");
      };
    },
  },
  methods: {
    async loadOptions() {
      try {
        const response = await this.$http.get("/api/v1/options.json", {
          params: { targets: ["ltls", "trailers", "trucks"] },
        });
        this.options.trailerTypes = response.data.ltls.trailer_types;
        this.options.trailers = response.data.trailers;
        this.options.trucks = response.data.trucks;
      } catch (err) {}
    },
    async loadBols() {
      try {
        const response = await this.$http.get("/api/v1/bols", {
          params: { q: { id_in: this.bolIds } },
        });
        this.bols = response.data.bols;
      } catch (err) {}
    },
    async loadCompanies() {
      try {
        const response = await this.$http.get("/api/v1/companies", {
          params: { q: { id_in: this.companyIds }, mode: "details" },
        });
        this.companies = response.data;
      } catch (err) {}
    },
    async loadLtl() {
      const url = this.id > 0 ? `/api/v1/ltls/${this.id}` : "/api/v1/ltls/new";
      try {
        const response = await this.$http.get(url);
        this.container = response.data;
        await this.loadTruckers();
        this.$nextTick(() => {
          this.inited = true;
        });
      } catch (err) {
        console.log(err);
      }
    },
    async saveLtl() {
      this.errors = {};
      const func = this.id > 0 ? this.$http.put : this.$http.post;
      const url = this.id > 0 ? `/api/v1/ltls/${this.id}` : `/api/v1/ltls`;
      try {
        const response = await func(url, { container: this.recordAttrs });
        const data = response.data;
        if (window.parent.LtlList) window.parent.LtlList.syncItem(data);
        if (this.turboFrame) {
          let url = `/ltl_containers/${data.id}?frame=${this.turboFrame.id}&layout=empty`;
          this.turboFrame.setAttribute("src", url);
        } else {
          window.location.href = `/ltl_containers/${data.id}`;
        }
      } catch (err) {
        this.errors = err.response.data.errors;
      }
    },
    findBol(operation) {
      return this.bols.find((o) => o.id == operation.bol_id);
    },
    bolDetails(operation) {
      return this.findBol(operation) || { customer: { name: "" } };
    },
    async loadTruckers() {
      const promises = this.container.transports.map(async (t) => {
        try {
          const response = await this.$http.get("/api/v1/transports/truckers", {
            params: {
              id: t.id,
              appt_date: this.container.appt_date,
            },
          });
          this.$set(t, "truckers", response.data);
        } catch (err) {}
      });
      await Promise.all(promises);
    },
    companyAddress(operation, opts = {}) {
      if (operation.company_id) {
        let company = this.findCompany(operation);
        if (this.isEmptyData(company)) return null;
        if (opts.html) {
          return `<div>${company.street}<br/>${company.city}, ${company.state}, ${company.zip}</div>`;
        } else {
          return `${company.street}, ${company.city}, ${company.state}, ${company.zip}`;
        }
      }
    },
    companyOptions(operation) {
      const company = this.findCompany(operation);
      return { id: company.id, name: company.name, type: company.type };
    },
    moveOperation(evt, seq) {
      if (evt.added) this.$set(evt.added.element, "sequence", seq);
      this.rebuildPositions();
    },
    rebuildPositions() {
      var pos = 1;
      this.transport.operations.forEach((o) => {
        this.$set(o, "pos", pos);
        pos += 1;
      });
    },
    async newCharge(company_id, sequence) {
      const options = { company_id, sequence };
      await PayableChargeForm.addCharge(options);
    },
    activeOperation(operation) {
      return operation._destroy != "1";
    },
    removableOperation(operation) {
      return (
        this.operations
          .filter((o) => o.bol_id == operation.bol_id)
          .filter((o) => !o.removable).length == 0
      );
    },
    deleteOperation(operation) {
      this.transport.operations
        .filter((o) => o.bol_id == operation.bol_id)
        .forEach((o) => this.$set(o, "_destroy", "1"));
    },
    addTrips(id, trips) {
      this.transport.operations
        .filter((o) => o.bol_id == id)
        .forEach((o) => this.$set(o, "_destroy", "1"));
      trips.forEach((trip) =>
        this.transport.operations.push(
          Object.assign({}, trip, { sequence: this.transport.sequence }),
        ),
      );
      this.transport.operations.forEach(
        (operation, index) => (operation.pos = index + 1),
      );
      this.loadBols();
      this.loadCompanies();
    },
  },
};
</script>
<style lang="scss" scoped>
.routes {
  @apply border-2 border-orange-400 divide-y divide-orange-300 bg-orange-100 #{!important};
  .header {
    @apply flex items-center justify-between bg-white border-b border-orange-300 px-3;
  }
  .stop {
    @apply relative pl-8 pr-1 py-2 border-b border-orange-300;
  }
}
</style>
